import { formatMoney } from "./api";

/**
 * Keep each item on its own table row, even when its name contains a separator. Lines booked
 * to a category, such as a split payment's parts, are spending, so no language is asked of them.
 */
export function BankDocumentItems({
  items,
  expenseItems = [],
  compact = false,
}: {
  readonly items: readonly string[];
  readonly expenseItems?: readonly number[] | undefined;
  /** Tighter rows, for the hover card, so a long list still fits on the screen. */
  readonly compact?: boolean;
}): React.ReactElement {
  const pad = compact ? "!py-1" : "py-2";
  const rows = items.map((item) => {
    const match = /^(.*) \| Qty: ([^|]+) \| Unit: SGD ([^|]+) \| Total: SGD ([^|]+)$/.exec(item);
    return match ? { name: match[1]!, quantity: match[2]!, price: match[3]!, amount: match[4]! } : { name: item, quantity: "", price: "", amount: "" };
  });
  return <table className="w-full text-[0.8125rem] mb-3" aria-label="Linked document items">
    <thead><tr className="border-b border-defaultborder text-[#8c9097] dark:text-white/50"><th className="text-start py-2 font-medium">Item</th><th className="text-end px-2 py-2 font-medium">Qty</th><th className="text-end px-2 py-2 font-medium whitespace-nowrap">Price S$</th><th className="text-end ps-2 py-2 font-medium whitespace-nowrap">Amount S$</th></tr></thead>
    <tbody>{rows.map((row, index) => <tr key={index} className="border-b border-defaultborder"><td className={`${pad} pe-2 break-words`}>{row.name}{row.quantity && !expenseItems.includes(index) && !/^(?:EN|JP|CN|KR)[\s-]/i.test(row.name) ? <div className="text-warning text-[0.6875rem]">Language not set. Check the product or graded card details.</div> : null}</td><td className={`text-end px-2 ${pad} whitespace-nowrap tabular-nums align-top`}>{row.quantity}</td><td className={`text-end px-2 ${pad} whitespace-nowrap tabular-nums align-top`}>{row.price}</td><td className={`text-end ps-2 ${pad} whitespace-nowrap tabular-nums align-top`}>{row.amount}</td></tr>)}</tbody>
  </table>;
}

/**
 * Between the items and the total: what the items came to and what was taken off or added
 * on the whole sale (D, 15 October 2026: a 125.00 bundle sold for 120.00, "if the government
 * ask, we have to answer questions"). Nothing when there was neither.
 */
export function DocumentAdjustments({
  subtotalCents,
  discountCents,
  surchargeCents,
}: {
  readonly subtotalCents?: number | null | undefined;
  readonly discountCents?: number | null | undefined;
  readonly surchargeCents?: number | null | undefined;
}): React.ReactElement | null {
  if (!discountCents && !surchargeCents) return null;
  return (
    <div className="text-end text-[0.8125rem] mb-1">
      {subtotalCents !== null && subtotalCents !== undefined ? <p className="mb-0">Items S$ {formatMoney(subtotalCents)}</p> : null}
      {discountCents ? <p className="mb-0 text-warning">Discount on the sale -S$ {formatMoney(discountCents)}</p> : null}
      {surchargeCents ? <p className="mb-0">Added on the sale +S$ {formatMoney(surchargeCents)}</p> : null}
    </div>
  );
}
